import React, { useEffect, useState, useCallback, useRef } from "react";
import Navbar from "../../components/Layout/Navbar";
import axios from "axios";
import TodoCard from "../../components/TodoCard/TodoCardList";
import toast from "react-hot-toast";
import "./TodoList.css";

const TodoList = () => {
  const [todos, setTodos] = useState([]);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("incomplete");
  const [showDropdown, setShowDropdown] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [sortBy, setSortBy] = useState("newest"); // newest, oldest, alphabetical
  const dropdownRef = useRef(null);

  const BASE_URL = process.env.REACT_APP_BACKEND_URL || "http://localhost:5000";

  const user = (() => {
    try {
      return JSON.parse(localStorage.getItem("todoapp")) || null;
    } catch (e) {
      return null;
    }
  })();
  const token = user?.token;

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const fetchTodos = useCallback(async () => {
    if (!token) return;
    setIsLoading(true);
    try {
      const res = await axios.get(`${BASE_URL}/todo/`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setTodos(res.data.todos || []);
    } catch (error) {
      console.error(error);
      toast.error("Failed to load tasks.");
    } finally {
      setIsLoading(false);
    }
  }, [token, BASE_URL]);

  useEffect(() => {
    fetchTodos();
  }, [fetchTodos]);

  // Filter and sort todos
  const getFilteredAndSortedTodos = () => {
    let filtered = todos.filter((t) => {
      const matchesSearch = t.title.toLowerCase().includes(search.toLowerCase()) ||
                           t.description.toLowerCase().includes(search.toLowerCase());
      const matchesFilter =
        filter === "all" ||
        (filter === "complete" && t.isCompleted) ||
        (filter === "incomplete" && !t.isCompleted);
      return matchesSearch && matchesFilter;
    });

    // Sort todos
    const sorted = [...filtered].sort((a, b) => {
      switch (sortBy) {
        case "newest":
          return new Date(b.createdAt) - new Date(a.createdAt);
        case "oldest":
          return new Date(a.createdAt) - new Date(b.createdAt);
        case "alphabetical":
          return a.title.localeCompare(b.title);
        default:
          return 0;
      }
    });

    return sorted;
  };

  const filteredTodos = getFilteredAndSortedTodos();

  // Calculate stats
  const stats = {
    total: todos.length,
    completed: todos.filter((t) => t.isCompleted).length,
    pending: todos.filter((t) => !t.isCompleted).length,
    filtered: filteredTodos.length,
  };

  const filterOptions = [
    { value: "all", label: "All Tasks", icon: "📋", color: "#667eea" },
    { value: "incomplete", label: "In Progress", icon: "⏳", color: "#f59e0b" },
    { value: "complete", label: "Completed", icon: "✅", color: "#10b981" },
  ];

  const sortOptions = [
    { value: "newest", label: "Newest First", icon: "🆕" },
    { value: "oldest", label: "Oldest First", icon: "📅" },
    { value: "alphabetical", label: "A-Z", icon: "🔤" },
  ];

  const clearSearch = () => {
    setSearch("");
  };

  const currentFilter = filterOptions.find((opt) => opt.value === filter);

  return (
    <>
      <Navbar />
      <div className="todo-list-container">
        {/* Header Section */}
        <div className="list-header">
          <div className="header-top">
            <h1 className="page-title">
              <span className="title-icon">📝</span>
              Task Manager
            </h1>
            <button className="refresh-btn" onClick={fetchTodos} title="Refresh tasks">
              <span className="refresh-icon">🔄</span>
            </button>
          </div>

          {/* Stats Dashboard */}
          <div className="stats-grid">
            <div className="stat-item stat-total">
              <div className="stat-icon">📊</div>
              <div className="stat-content">
                <span className="stat-value">{stats.total}</span>
                <span className="stat-label">Total Tasks</span>
              </div>
            </div>
            <div className="stat-item stat-pending">
              <div className="stat-icon">⏳</div>
              <div className="stat-content">
                <span className="stat-value">{stats.pending}</span>
                <span className="stat-label">In Progress</span>
              </div>
            </div>
            <div className="stat-item stat-completed">
              <div className="stat-icon">✅</div>
              <div className="stat-content">
                <span className="stat-value">{stats.completed}</span>
                <span className="stat-label">Completed</span>
              </div>
            </div>
            {stats.completed > 0 && (
              <div className="stat-item stat-progress">
                <div className="stat-icon">📈</div>
                <div className="stat-content">
                  <span className="stat-value">
                    {Math.round((stats.completed / stats.total) * 100)}%
                  </span>
                  <span className="stat-label">Progress</span>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Controls Section */}
        <div className="controls-wrapper">
          {/* Search Bar */}
          <div className="search-container">
            <span className="search-icon">🔍</span>
            <input
              type="search"
              placeholder="Search by title or description..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="search-input"
            />
            {search && (
              <button className="clear-search" onClick={clearSearch}>
                ✕
              </button>
            )}
          </div>

          {/* Filter and Sort Controls */}
          <div className="filter-sort-controls">
            {/* Filter Dropdown */}
            <div className="custom-dropdown" ref={dropdownRef}>
              <button
                className="dropdown-trigger"
                onClick={() => setShowDropdown(!showDropdown)}
                style={{ borderColor: currentFilter?.color }}
              >
                <span className="dropdown-icon">{currentFilter?.icon}</span>
                <span className="dropdown-label">{currentFilter?.label}</span>
                <span className={`dropdown-arrow ${showDropdown ? "rotate" : ""}`}>
                  ▼
                </span>
              </button>

              {showDropdown && (
                <div className="dropdown-menu">
                  {filterOptions.map((option) => (
                    <div
                      key={option.value}
                      className={`dropdown-item ${filter === option.value ? "active" : ""}`}
                      onClick={() => {
                        setFilter(option.value);
                        setShowDropdown(false);
                      }}
                      style={{
                        "--accent-color": option.color,
                      }}
                    >
                      <span className="item-icon">{option.icon}</span>
                      <span className="item-label">{option.label}</span>
                      {filter === option.value && (
                        <span className="check-icon">✓</span>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Sort Buttons */}
            <div className="sort-buttons">
              {sortOptions.map((option) => (
                <button
                  key={option.value}
                  className={`sort-btn ${sortBy === option.value ? "active" : ""}`}
                  onClick={() => setSortBy(option.value)}
                  title={option.label}
                >
                  <span className="sort-icon">{option.icon}</span>
                  <span className="sort-label">{option.label}</span>
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Results Info */}
        {search && (
          <div className="results-info">
            <span className="results-text">
              Found <strong>{stats.filtered}</strong> task{stats.filtered !== 1 ? "s" : ""} 
              matching "<strong>{search}</strong>"
            </span>
          </div>
        )}

        {/* Todo List */}
        <div className="todo-grid">
          {isLoading ? (
            <div className="loading-state">
              <div className="loader-spinner"></div>
              <p className="loading-text">Loading your tasks...</p>
            </div>
          ) : filteredTodos.length === 0 ? (
            <div className="empty-state">
              <div className="empty-illustration">
                {search ? "🔍" : filter === "complete" ? "🎉" : "📝"}
              </div>
              <h3 className="empty-title">
                {search
                  ? "No matching tasks"
                  : filter === "complete"
                  ? "No completed tasks yet"
                  : filter === "incomplete"
                  ? "No pending tasks"
                  : "No tasks created yet"}
              </h3>
              <p className="empty-description">
                {search
                  ? "Try adjusting your search or filters"
                  : filter === "complete"
                  ? "Complete some tasks to see them here!"
                  : "Start by creating your first task"}
              </p>
              {search && (
                <button className="clear-filters-btn" onClick={clearSearch}>
                  Clear Search
                </button>
              )}
            </div>
          ) : (
            filteredTodos.map((todo, index) => (
              <div
                key={todo._id}
                className="todo-item-wrapper"
                style={{ animationDelay: `${index * 0.05}s` }}
              >
                <TodoCard todo={todo} />
              </div>
            ))
          )}
        </div>

        {/* Footer Stats */}
        {!isLoading && filteredTodos.length > 0 && (
          <div className="list-footer">
            <p className="footer-text">
              Showing {filteredTodos.length} of {stats.total} task
              {stats.total !== 1 ? "s" : ""}
            </p>
          </div>
        )}
      </div>
    </>
  );
};

export default TodoList;